import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ReportShell from './ReportShell';
import useReport from './useReport';

const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const num = (n) => (n ?? 0).toLocaleString();
/** n of d as a whole percent; no denominator is "—", not 0%. */
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : '—');

const STATUS_LABELS = {
  queued: 'Scheduled',
  draft: 'Draft',
  inProcess: 'Sending',
  suspended: 'Suspended',
  inReview: 'In review',
  archive: 'Archived',
  cancelled: 'Cancelled',
};

/** A rate with enough precision to tell 0.01% from 0%; "—" without a denominator. */
const ratePct = (n, d) => {
  if (!d) return '—';
  const r = (n / d) * 100;
  return `${r < 1 ? r.toFixed(2) : r.toFixed(1)}%`;
};

const sum = (rows, k) => rows.reduce((n, c) => n + (c[k] || 0), 0);

/** All-time totals across every sent campaign, in the Activation tab's tile style. */
function CampaignTotals({ sent }) {
  const t = {};
  ['sent', 'delivered', 'hard_bounces', 'soft_bounces', 'complaints', 'unsubscribes', 'confirmed_opens', 'clickers']
    .forEach((k) => { t[k] = sum(sent, k); });
  const bounces = t.hard_bounces + t.soft_bounces;
  // Mailbox-provider lines: hard bounces under 2%, complaints under 0.1%.
  const highBounce = t.sent && t.hard_bounces / t.sent > 0.02;
  const highSpam = t.delivered && t.complaints / t.delivered > 0.001;
  const tiles = [
    { key: 'bounced', label: 'Bounced', value: ratePct(bounces, t.sent),
      sub: `${num(t.hard_bounces)} hard · ${num(t.soft_bounces)} soft`, warn: highBounce },
    { key: 'spam', label: 'Spam complaints', value: ratePct(t.complaints, t.delivered),
      sub: `${num(t.complaints)} · keep under 0.1%`, warn: highSpam },
    { key: 'unsub', label: 'Unsubscribed', value: ratePct(t.unsubscribes, t.delivered),
      sub: `${num(t.unsubscribes)} people` },
    { key: 'opened', label: 'Opened', value: ratePct(t.confirmed_opens, t.delivered),
      sub: `${num(t.confirmed_opens)} confirmed opens` },
    { key: 'clicked', label: 'Clicked', value: ratePct(t.clickers, t.delivered),
      sub: `${num(t.clickers)} people` },
  ];
  return (
    <div className="act-kpis act-kpis--six" data-testid="campaigns-totals">
      <div className="act-kpi act-kpi--hero">
        <div className="act-kpi__label">Delivered · all time</div>
        <div className="act-kpi__value">{ratePct(t.delivered, t.sent)}</div>
        <div className="act-kpi__sub">
          <strong>{num(t.delivered)} of {num(t.sent)}</strong> across {sent.length} campaign{sent.length === 1 ? '' : 's'}
        </div>
      </div>
      {tiles.map((x) => (
        <div className="act-kpi" key={x.key} data-testid={`campaigns-total-${x.key}`}>
          <div className="act-kpi__top">
            <span className="act-kpi__label">{x.label}</span>
            {x.warn && <span className="adm-chip adm-chip--amber">High</span>}
          </div>
          <div className="act-kpi__value">{x.value}</div>
          <div className="act-kpi__sub">{x.sub}</div>
        </div>
      ))}
    </div>
  );
}

function CampaignRow({ c }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr data-testid={`campaign-${c.id}`}>
        <td>
          <button type="button" className="campaign-toggle" onClick={() => setOpen((o) => !o)}
            aria-expanded={open} style={{ all: 'unset', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600 }}>{open ? '▾' : '▸'} {c.name}</div>
            <div className="report-note" style={{ margin: 0 }}>{c.subject}</div>
          </button>
        </td>
        <td>{fmtDate(c.sent_at)}</td>
        <td>{num(c.sent)}</td>
        <td>{num(c.delivered)}</td>
        <td title={`${num(c.hard_bounces)} hard, ${num(c.soft_bounces)} soft`}>{num(c.hard_bounces + c.soft_bounces)}</td>
        <td>
          {num(c.confirmed_opens)} <span className="report-note">({pct(c.confirmed_opens, c.delivered)})</span>
          <div className="report-note" style={{ margin: 0 }}>{num(c.opens)} incl. Apple</div>
        </td>
        <td>{num(c.clickers)} <span className="report-note">({pct(c.clickers, c.delivered)})</span></td>
        <td>{num(c.site_clicks)}</td>
        <td>{num(c.unsubscribes)}</td>
        <td>{num(c.complaints)}</td>
        <td><strong>{num(c.signups)}</strong></td>
        <td><strong>{num(c.inquiries)}</strong></td>
      </tr>
      {open && (
        <tr>
          <td colSpan={12} style={{ background: '#F5F8F6' }}>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 360px' }}>
                <h4>Clicks per link</h4>
                <table className="report-table" data-testid={`campaign-links-${c.id}`}>
                  <tbody>
                    {c.links.map((l) => (
                      <tr key={l.url}>
                        <td style={{ wordBreak: 'break-all' }}>
                          {l.url}{l.social && <span className="report-note"> (footer social)</span>}
                        </td>
                        <td>{num(l.clicks)}</td>
                      </tr>
                    ))}
                    {c.links.length === 0 && <tr><td colSpan={2}>No links tracked.</td></tr>}
                  </tbody>
                </table>
                <p className="report-note">
                  Campaign tag{c.utm_campaigns.length === 1 ? '' : 's'}:{' '}
                  {c.utm_campaigns.length ? c.utm_campaigns.join(', ') : 'none — signups and inquiries cannot be counted for this email'}
                </p>
              </div>
              <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                <h4>Signups from this email</h4>
                {c.signup_users.length === 0 && <p className="report-note">None yet.</p>}
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {c.signup_users.map((u) => (
                    <li key={u.user_id}>
                      {u.role === 'athlete' ? <Link to={`/athletes/${u.user_id}`}>{u.name}</Link> : u.name}{' '}
                      <span className="report-note">{u.role} · {fmtDate(u.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Brevo email campaigns (prozpects.com marketing blasts), read live from the
 * Brevo API by the backend and cached there for ten minutes. Signups and
 * inquiries are matched on the utm_campaign tag in each email's links.
 */
export default function CampaignsReport() {
  const [refreshTick, setRefreshTick] = useState(0);
  const { data, loading, error } = useReport(
    'campaigns', 'all', refreshTick ? `&refresh=true&t=${refreshTick}` : '',
  );

  const all = data?.campaigns || [];
  const sent = all.filter((c) => c.status === 'sent');
  const pending = all.filter((c) => c.status !== 'sent');

  let body;
  if (data && data.configured === false) {
    body = (
      <p className="report-error" data-testid="campaigns-not-configured">
        Brevo is not connected: BREVO_API_KEY is not set on this server.
      </p>
    );
  } else if (data?.error) {
    body = (
      <p className="report-error" data-testid="campaigns-brevo-error">
        Could not load campaigns from Brevo ({data.error}).
      </p>
    );
  } else {
    body = (
      <>
        {sent.length > 0 && <CampaignTotals sent={sent} />}
        <div className="report-table-wrap">
          <table className="report-table" data-testid="campaigns-sent">
            <thead>
              <tr>
                <th>Campaign</th><th>Sent on</th><th>Sent</th><th>Delivered</th><th>Bounced</th>
                <th>Opened</th><th>Clicked</th><th>Link clicks</th><th>Unsub</th><th>Spam</th>
                <th>Signups</th><th>Inquiries</th>
              </tr>
            </thead>
            <tbody>
              {sent.map((c) => <CampaignRow key={c.id} c={c} />)}
              {sent.length === 0 && <tr><td colSpan={12}>No campaigns sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="report-note">
          Opened counts only opens Brevo could confirm; &quot;incl. Apple&quot; adds Apple Mail Privacy
          opens, which are machine-generated. Clicked is people who clicked anything; Link clicks is
          total clicks on our own links (footer social icons excluded). Signups and Inquiries are app
          accounts and prozpects.com form submissions that arrived carrying the email&apos;s campaign
          tag. Click a campaign for per-link clicks and who signed up.
        </p>

        {pending.length > 0 && (
          <>
            <h3>Not sent yet</h3>
            <table className="report-table" data-testid="campaigns-pending">
              <thead><tr><th>Campaign</th><th>Status</th><th>Scheduled for</th></tr></thead>
              <tbody>
                {pending.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{STATUS_LABELS[c.status] || c.status}</td>
                    <td>{c.status === 'queued' ? fmtTime(c.scheduled_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </>
    );
  }

  return (
    <ReportShell
      title="Campaigns"
      showPopulation={false}
      populationSize={sent.length}
      populationNoun={sent.length === 1 ? 'Brevo email campaign' : 'Brevo email campaigns'}
      loading={loading}
      error={error}
    >
      <p className="report-note">
        Live from Brevo{data?.fetched_at ? `, as of ${fmtTime(data.fetched_at)}` : ''}.{' '}
        <button type="button" onClick={() => setRefreshTick((n) => n + 1)} data-testid="campaigns-refresh">
          Refresh
        </button>
      </p>
      {body}
    </ReportShell>
  );
}
